import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    OnChanges,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    FormBuilder,
    Validators,
    FormControl,
    FormGroup,
    FormArray,
    ValidationErrors,
    ValidatorFn
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
    MatNativeDateModule,
    MAT_DATE_LOCALE
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { PlantLocationComponent } from './plant-location-component/plant-location-component.component';
import {
    atLeastOneMeteringPoint,
    cityIdFromAutocompleteValidator,
    exactlyOneMain,
    latitudeValidator,
    locationValidator,
    longitudeValidator,
    requirePlantIdValidator
} from '../shared/validators';
import { PlantsService } from '../services/plants.service';
import { PlantDto } from '../Models/plantDto.interface';
import { SelectedLocation } from '../Models/selectLocation.interface';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    Observable,
    skip,
    startWith,
    switchMap,
    of,
    catchError,
    take
} from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AddNewCityModalComponent } from './add-new-city-modal/add-new-city-modal.component';
import { UploadPlantImageComponent } from './upload-plant-image/upload-plant-image.component';
import { CityService } from '../services/city.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UsersService } from '../../users-page/users-service/users.service';
import {
    installationDateFromString,
    installationDateToString
} from '../shared/installation-date.util';

type UserContext = {
    userId: number;
    fullName?: string;
    userName?: string;
    email?: string;
    phoneNumber?: string;
};

type MeteringPoint = {
    meteringPointId: string;
    meterType: string;
    isMain: boolean;
};

type MpRowForm = {
    meteringPointId: FormControl<string>;
    meterType: FormControl<string>;
    isMain: FormControl<boolean>;
    meterTypeSearch: FormControl<string>;
};

type InverterFormModel = {
    brand: FormControl<string>;
    batteryChargePowerRateKw: FormControl<string>;
    serialNumber: FormControl<string>;
    maxProductionCapacity: FormControl<string>;
    meteringPointIds: FormControl<MeteringPoint[]>;
    model: FormControl<string>;
    plantId: FormControl<number>;
    totalBatteryCapacityKwhsolarPanels: FormControl<string>;
    totalPanelPowerKwh: FormControl<string>;
    solarFactorThreshold: FormControl<string>;
    meteringPoints: FormArray<FormGroup<MpRowForm>>;
};

type CityOption = { id: number; title: string; timeZoneId: string };

@Component({
    selector: 'app-add-plant',
    standalone: true,
    imports: [
        RouterModule,
        ReactiveFormsModule,
        FormsModule,
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatStepperModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        MatRadioModule,
        MatDialogModule,
        PlantLocationComponent,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatNativeDateModule,
        UploadPlantImageComponent,
        TranslatePipe
    ],
    templateUrl: './add-plant.component.html',
    providers: [
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB'
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './add-plant.component.scss'
})
export class AddPlantComponent implements OnInit, OnChanges {
    @Input() mode: 'add' | 'update' = 'add';
    @Input() initialData: any = null;
    @Output() submitForm = new EventEmitter<any>();

    userId: number | null = null;
    fullName: string | null = null;
    userName: string | null = null;
    email: string | null = null;
    phoneNumber: string | null = null;

    @Input() currentPlantId: number | null = null;
    @Input() firstStepCompleted: boolean = false;
    @Input() userContext?: UserContext;
    @Input() plantId?: number;

    @ViewChild(MatStepper) stepper!: MatStepper;
    @ViewChild(UploadPlantImageComponent) fileHold?: UploadPlantImageComponent;
    @ViewChild(MatAutocompleteTrigger)
    private cityAutoTrigger?: MatAutocompleteTrigger;
    @ViewChild('cityInput', { read: ElementRef })
    private cityInput?: ElementRef<HTMLInputElement>;

    isSubmittingInverter = false;
    isSubmitting = false;
    isNewCityMode = false;

    private brandToModels = new Map<string, string[]>();
    private brandChangeHooked = false;
    private optionsLoaded = false;
    private pendingCustomerType: string | null = null;

    private readonly solarThresholdMin = 0;
    private readonly solarThresholdMax = 1;

    brandOptions: string[] = [];
    modelOptions: string[] = [];
    meterTypeOptions: { title: string; description?: string }[] = [];
    customerTypeOptions: string[] = [];

    brandSearchCtrl = new FormControl<string>('', { nonNullable: true });
    modelSearchCtrl = new FormControl<string>('', { nonNullable: true });
    citySearchCtrl = new FormControl<string | CityOption | null>('');

    filteredBrands$: Observable<string[]> = of([]);
    filteredModels$: Observable<string[]> = of([]);
    filteredCities$!: Observable<CityOption[]>;

    selectedCity?: CityOption;

    selectedFile: File | null = null;
    isUploadingImage = false;
    uploadProgress = 0;
    uploadError: any | null = null;
    uploadedImageId: string | null = null;
    uploadedImageUrl: string | null = null;
    primaryImageId?: string;
    fileHandle: string | null = null;
    readonly today = new Date();
    priceClass: any[] = [{ priceClass: 'DK1' }, { priceClass: 'DK2' }];

    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private usersService: UsersService,
        private route: ActivatedRoute,
        private sb: MatSnackBar,
        private router: Router,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private cityService: CityService,
        private translate: TranslateService
    ) { }

    displayMeterType = (
        v?: { title: string; description?: string } | string | null
    ) => (typeof v === 'string' ? v : v?.title ?? '');

    get isLocked(): boolean {
        return this.mode === 'add' && !!this.firstStepCompleted;
    }

    get selectedTimeZoneId(): string | undefined {
        return this.selectedCity?.timeZoneId;
    }

    get meteringPointsFA(): FormArray<FormGroup<MpRowForm>> {
        return this.invertersForm.controls.meteringPoints;
    }

    private t(key: string, params?: Record<string, unknown>): string {
        return this.translate.instant(key, params);
    }

    personalForm = this.fb.group({
        plantOwner: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.email, Validators.required]],
        phone: ['', Validators.required],
        address: ['', Validators.required],
        city: [''],
        priceClass: this.fb.control<string | null>(null, [Validators.required]),
        lowPriceThreshold: this.fb.control<string | null>(null, [
            Validators.required
        ]),
        customerType: ['', Validators.required],
        userId: this.fb.control<number | null>(null),
        username: ['', Validators.required],
        plantImageUrl: this.fb.control<string | null>(null),
        location: this.fb.control<SelectedLocation | null>(null, [
            locationValidator()
        ]),
        latitude: this.fb.control<number | null>(null, [
            Validators.required,
            latitudeValidator()
        ]),
        longitude: this.fb.control<number | null>(null, [
            Validators.required,
            longitudeValidator()
        ]),
        cityId: this.fb.control<number | null>(null, [
            Validators.required,
            cityIdFromAutocompleteValidator(() => this.selectedCity)
        ]),
        timeZoneId: this.fb.control<string | null>(null),
        installationDate: this.fb.control<Date | null>(null),
    });

    invertersForm = this.fb.group<InverterFormModel>({
        brand: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        batteryChargePowerRateKw: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        serialNumber: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        maxProductionCapacity: this.fb.control('', { nonNullable: true }),
        meteringPointIds: this.fb.control<MeteringPoint[]>([], {
            nonNullable: true
        }),
        model: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        plantId: this.fb.control(0, { nonNullable: true }),
        totalBatteryCapacityKwhsolarPanels: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        totalPanelPowerKwh: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required]
        }),
        solarFactorThreshold: this.fb.control('', {
            nonNullable: true,
            validators: [Validators.required, this.solarThresholdValidator()]
        }),
        meteringPoints: this.fb.array<FormGroup<MpRowForm>>([])
    });

    ngOnInit(): void {
        this.getUserData();
        this.applyUserContext(this.userContext);
        const locCtrl = this.personalForm.get('location')!;
        const latCtrl = this.personalForm.get('latitude')!;
        const lngCtrl = this.personalForm.get('longitude')!;

        const project = (loc: SelectedLocation | null) => {
            if (
                loc &&
                typeof loc.lat === 'number' &&
                typeof loc.lng === 'number'
            ) {
                latCtrl.setValue(+loc.lat.toFixed(6), { emitEvent: false });
                lngCtrl.setValue(+loc.lng.toFixed(6), { emitEvent: false });
            } else {
                latCtrl.reset(null, { emitEvent: false });
                lngCtrl.reset(null, { emitEvent: false });
            }

            latCtrl.updateValueAndValidity({
                onlySelf: true,
                emitEvent: false
            });
            lngCtrl.updateValueAndValidity({
                onlySelf: true,
                emitEvent: false
            });
        };

        project(locCtrl.value as SelectedLocation | null);
        locCtrl.valueChanges.subscribe((loc: SelectedLocation | null) =>
            project(loc)
        );

        this.hookBrandChange();
        this.searchComboBox();
        this.searchCityComboBox();
        this.loadInverterPrerequisites();

        this.brandSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm
                .get('brand')!
                .setValue(v ?? '', { emitEvent: false });
            this.invertersForm
                .get('brand')!
                .updateValueAndValidity({ onlySelf: true });
        });

        this.modelSearchCtrl.valueChanges.subscribe(v => {
            this.invertersForm
                .get('model')!
                .setValue(v ?? '', { emitEvent: false });
            this.invertersForm
                .get('model')!
                .updateValueAndValidity({ onlySelf: true });
        });

        const preset = this.invertersForm.get('meteringPointIds')?.value as
            | MeteringPoint[]
            | null;
        if (preset?.length) {
            preset.forEach(mp =>
                this.meteringPointsFA.push(this.createMeteringPointRow(mp))
            );
        }

        if (this.meteringPointsFA.length === 0) {
            this.addMeteringPoint();
        }

        this.invertersForm.setValidators([
            requirePlantIdValidator(),
            atLeastOneMeteringPoint(),
            exactlyOneMain()
        ]);
        this.invertersForm.updateValueAndValidity();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialData'] && changes['initialData'].currentValue) {
            if (this.mode === 'update') {
                this.firstStepCompleted = true;
            }
            this.applyInitialData(changes['initialData'].currentValue);
        }

        if (changes['mode'] && changes['mode'].currentValue) {
            if (this.mode === 'update') {
                this.firstStepCompleted = true;
            }
        }
    }

    private createFilter$(
        ctrl: FormControl<string>,
        getOptions: () => string[]
    ): Observable<string[]> {
        return ctrl.valueChanges.pipe(
            startWith(ctrl.value ?? ''),
            map(v => (v ?? '').toLowerCase()),
            map(q => getOptions().filter(x => x.toLowerCase().includes(q)))
        );
    }

    private solarThresholdValidator(
        min = this.solarThresholdMin,
        max = this.solarThresholdMax
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const raw = String(control.value ?? '').trim();

            if (!raw) {
                return null;
            }

            const normalized = raw.replace(',', '.');
            const parsed = Number(normalized);

            if (Number.isNaN(parsed)) {
                return { solarThresholdInvalid: true };
            }

            if (parsed < min || parsed > max) {
                return {
                    solarThresholdOutOfRange: {
                        min,
                        max,
                        actual: parsed
                    }
                };
            }

            return null;
        };
    }

    private normalizeSolarFactorThreshold(value: any): string {
        if (Array.isArray(value)) {
            return String(value[0] ?? '')
                .trim()
                .replace(',', '.');
        }

        return String(value ?? '')
            .trim()
            .replace(',', '.');
    }

    private getFriendlyErrorMessage(
        err: any,
        context:
            | 'add-inverter'
            | 'add-plant'
            | 'update-plant'
            | 'prerequisites'
            | 'general' = 'general'
    ): string {
        const raw =
            String(err?.statusMessage ?? '') ||
            String(err?.message ?? '') ||
            String(err?.error?.message ?? '') ||
            this.t('something_went_wrong');

        const message = raw.toLowerCase();

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('out of range')
        ) {
            return this.t('solar_threshold_out_of_range');
        }

        if (
            message.includes('solarfactorthreshold') &&
            message.includes('not valid')
        ) {
            return this.t('solar_threshold_invalid_numeric_value');
        }

        if (
            message.includes('exactly one metering point') ||
            message.includes('maincountinvalid')
        ) {
            return this.t('exactly_one_metering_point_main');
        }

        if (message.includes('invalidargument')) {
            switch (context) {
                case 'add-inverter':
                    return this.t('some_inverter_fields_invalid');
                case 'add-plant':
                    return this.t('some_plant_fields_invalid');
                case 'update-plant':
                    return this.t('plant_update_contains_invalid_values');
                case 'prerequisites':
                    return this.t('failed_load_prerequisites');
                default:
                    return this.t('some_submitted_values_invalid');
            }
        }

        return raw;
    }

    private handleError(
        err: any,
        context:
            | 'add-inverter'
            | 'add-plant'
            | 'update-plant'
            | 'prerequisites'
            | 'general' = 'general'
    ): void {
        console.error(`[${context}]`, err);
        this.notify(this.getFriendlyErrorMessage(err, context), 'error');
    }

    private validateSolarThresholdBeforeSubmit(): boolean {
        const ctrl = this.invertersForm.controls.solarFactorThreshold;

        ctrl.updateValueAndValidity({ onlySelf: true });

        if (ctrl.hasError('required')) {
            ctrl.markAsTouched();
            this.notify(this.t('solar_threshold_required'), 'error');
            return false;
        }

        if (ctrl.hasError('solarThresholdInvalid')) {
            ctrl.markAsTouched();
            this.notify(this.t('solar_threshold_invalid'), 'error');
            return false;
        }

        if (ctrl.hasError('solarThresholdOutOfRange')) {
            ctrl.markAsTouched();
            this.notify(this.t('solar_threshold_out_of_range'), 'error');
            return false;
        }

        return true;
    }

    private initAutocompleteFilters(): void {
        this.brandSearchCtrl.setValue(
            this.invertersForm.get('brand')?.value ?? '',
            { emitEvent: false }
        );
        this.modelSearchCtrl.setValue(
            this.invertersForm.get('model')?.value ?? '',
            { emitEvent: false }
        );

        this.filteredBrands$ = this.createFilter$(
            this.brandSearchCtrl,
            () => this.brandOptions
        );
        this.filteredModels$ = this.createFilter$(
            this.modelSearchCtrl,
            () => this.modelOptions
        );

        const currentBrand = (
            this.invertersForm.get('brand')?.value ?? ''
        ).trim();
        this.onBrandChanged(currentBrand);
    }

    private reconcileCustomerType(): void {
        const ctrl = this.personalForm.get('customerType');
        const val = (ctrl?.value ?? '') as string;
        if (!val) return;

        if (!this.customerTypeOptions.includes(val)) {
            ctrl?.setValue(null, { emitEvent: false });
            ctrl?.updateValueAndValidity({ onlySelf: true });
        }
    }

    private setPrerequisiteOptionsAndInit(res: any) {
        this.brandToModels.clear();

        const brands = Array.isArray(res?.brands) ? res.brands : [];
        this.brandOptions = brands
            .map((b: any) => b?.title)
            .filter((t: any) => !!t);

        brands.forEach((b: any) => {
            const title = b?.title ?? '';
            const modelsArr = Array.isArray(b?.models) ? b.models : [];
            const modelTitles = modelsArr
                .map((m: any) => m?.title)
                .filter((t: any) => !!t);
            this.brandToModels.set(title, modelTitles);
        });

        const currentBrand = (
            this.invertersForm.get('brand')?.value ?? ''
        ).trim();
        this.modelOptions = this.brandToModels.get(currentBrand) ?? [];

        this.meterTypeOptions = (res.meterTypes ?? []).map((t: any) => ({
            title: t.title,
            description: t.description
        }));

        this.customerTypeOptions = (res.customerTypes ?? [])
            .map((ct: any) => ct.title)
            .filter(Boolean);

        this.optionsLoaded = true;
        this.initAutocompleteFilters();
        this.applyPendingCustomerType();
    }

    searchCityComboBox() {
        this.filteredCities$ = this.citySearchCtrl.valueChanges.pipe(
            startWith(this.citySearchCtrl.value ?? ''),
            debounceTime(250),
            distinctUntilChanged(),
            switchMap((q: string | CityOption | null) => {
                const query =
                    typeof q === 'string'
                        ? (q ?? '').trim()
                        : q && (q as CityOption).title
                            ? (q as CityOption).title.trim()
                            : '';

                return this.plantsSvc.getCities(query).pipe(
                    map(res =>
                        (res.cities ?? []).map(
                            (c: any) =>
                            ({
                                id: c.id!,
                                title: c.title ?? '',
                                timeZoneId: c.timeZoneId ?? ''
                            } as CityOption)
                        )
                    ),
                    catchError(err => {
                        console.error('getCities failed', err);
                        return of([] as CityOption[]);
                    })
                );
            })
        );

        this.citySearchCtrl.valueChanges
            .pipe(skip(1))
            .subscribe((val: string | CityOption | null) => {
                const valTitle = typeof val === 'string' ? val : val?.title;
                if (this.selectedCity && valTitle !== this.selectedCity.title) {
                    this.selectedCity = undefined;
                    this.personalForm.patchValue(
                        { cityId: null },
                        { emitEvent: false }
                    );
                    this.personalForm.get('cityId')!.updateValueAndValidity();
                }
            });

        this.personalForm
            .get('cityId')!
            .addValidators(
                cityIdFromAutocompleteValidator(() => this.selectedCity)
            );
    }

    onCityOptionSelected(ev: MatAutocompleteSelectedEvent) {
        const city = ev.option.value as CityOption;
        this.selectedCity = city;
        this.citySearchCtrl.setValue(city.title, { emitEvent: false });
        this.personalForm.patchValue({ cityId: city.id }, { emitEvent: false });
        this.personalForm.get('cityId')!.updateValueAndValidity();
    }

    searchComboBox() {
        this.plantsSvc.getAddInverterPrerequisites().subscribe({
            next: res => this.setPrerequisiteOptionsAndInit(res),
            error: err => this.handleError(err, 'prerequisites')
        });
    }

    getAddInverterPrerequisites() {
        this.plantsSvc.getAddInverterPrerequisites().subscribe({
            next: res => this.setPrerequisiteOptionsAndInit(res),
            error: err => this.handleError(err, 'prerequisites')
        });
    }

    private loadInverterPrerequisites() {
        if (this.brandOptions.length || this.modelOptions.length) {
            return;
        }

        this.plantsSvc.getAddInverterPrerequisites().subscribe({
            next: res => this.setPrerequisiteOptionsAndInit(res),
            error: err => this.handleError(err, 'prerequisites')
        });
    }

    private hookBrandChange() {
        if (this.brandChangeHooked) return;
        this.brandChangeHooked = true;

        const brandCtrl = this.invertersForm.get('brand')!;
        brandCtrl.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe((brand: string) =>
                this.onBrandChanged((brand ?? '').trim())
            );
    }

    private onBrandChanged(brand: string) {
        this.invertersForm.get('model')!.setValue('', { emitEvent: false });
        this.modelSearchCtrl.setValue('', { emitEvent: false });
        this.modelOptions = this.brandToModels.get(brand) ?? [];
        this.modelSearchCtrl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: true
        });
    }

    onBrandSelected(ev: MatAutocompleteSelectedEvent) {
        const val = (ev.option.value as string) ?? '';
        this.invertersForm.controls['brand']?.setValue(val);
        this.invertersForm.controls['brand']?.updateValueAndValidity();
        this.onBrandChanged(val.trim());
    }

    getMeterTypeFiltered(i: number) {
        const row = this.meteringPointsFA.at(i);
        return row.controls.meterType.valueChanges.pipe(
            startWith(row.controls.meterType.value ?? ''),
            map(v => (v ?? '').toLowerCase()),
            map(q =>
                this.meterTypeOptions.filter(
                    mt =>
                        mt.title.toLowerCase().includes(q) ||
                        (mt.description ?? '').toLowerCase().includes(q)
                )
            )
        );
    }

    addMeteringPoint() {
        const isFirst = this.meteringPointsFA.length === 0;
        this.meteringPointsFA.push(
            this.createMeteringPointRow({ isMain: isFirst })
        );
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    removeMeteringPoint(i: number) {
        this.meteringPointsFA.removeAt(i);
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    setAsMain(i: number) {
        this.meteringPointsFA.controls.forEach((g, idx) =>
            g.controls.isMain.setValue(idx === i)
        );
        this.syncMeteringPointsControl();
        this.invertersForm.updateValueAndValidity();
    }

    onMeterTypeSelected(i: number, ev: MatAutocompleteSelectedEvent) {
        const val = ev.option.value as
            | { title: string; description?: string }
            | string;
        const title = typeof val === 'string' ? val : val.title;
        const row = this.meteringPointsFA.at(i);
        row.controls.meterTypeSearch.setValue(title, { emitEvent: false });
        row.controls.meterType.setValue(title);
        row.controls.meterType.updateValueAndValidity();
    }

    private createMeteringPointRow(mp?: Partial<MeteringPoint>) {
        const row = this.fb.group<MpRowForm>({
            meteringPointId: this.fb.control(mp?.meteringPointId ?? '', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            meterType: this.fb.control(mp?.meterType ?? '', {
                nonNullable: true,
                validators: [Validators.required]
            }),
            isMain: this.fb.control(!!mp?.isMain, { nonNullable: true }),
            meterTypeSearch: this.fb.control('', { nonNullable: true })
        });

        row.controls.meterTypeSearch.valueChanges.subscribe(v => {
            row.controls.meterType.setValue(v ?? '', { emitEvent: false });
            row.controls.meterType.updateValueAndValidity({ onlySelf: true });
            this.syncMeteringPointsControl();
            this.invertersForm.updateValueAndValidity();
        });

        return row;
    }

    private syncMeteringPointsControl() {
        const arr: MeteringPoint[] = this.meteringPointsFA.controls.map(g => ({
            meteringPointId: g.controls.meteringPointId.value,
            meterType: g.controls.meterType.value,
            isMain: g.controls.isMain.value
        }));
        this.invertersForm.controls.meteringPointIds.setValue(arr);
    }

    getUserData(): void {
        const userId = Number(
            this.route.snapshot.paramMap.get('userId')
        );

        if (!userId || Number.isNaN(userId)) {
            console.warn('Invalid userId');
            return;
        }

        this.userId = userId;

        this.usersService
            .getUser(userId)
            .pipe(take(1))
            .subscribe({
                next: user => {
                    const fullName = [
                        user.firstName,
                        user.lastName
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .trim();

                    this.fullName = fullName;
                    this.userName = user.username ?? null;
                    this.email = user.email ?? null;
                    this.phoneNumber = user.phoneNumber ?? null;

                    this.personalForm.patchValue(
                        {
                            plantOwner: fullName,
                            username: user.username ?? '',
                            email: user.email ?? '',
                            phone: user.phoneNumber ?? '',
                            userId: user.id ?? userId
                        },
                        {
                            emitEvent: false
                        }
                    );
                },

                error: error => {
                    console.error(
                        'Failed to load user details:',
                        error
                    );
                }
            });
    }

    private buildAddInverterRequest() {
        this.syncMeteringPointsControl();

        return {
            batteryChargePowerRateKw: String(
                this.invertersForm.value.batteryChargePowerRateKw ?? ''
            ).trim(),
            brand: String(this.invertersForm.value.brand ?? '').trim(),
            maxProductionCapacity: String(
                this.invertersForm.value.maxProductionCapacity ?? ''
            ).trim(),
            solarFactorThreshold: this.normalizeSolarFactorThreshold(
                this.invertersForm.value.solarFactorThreshold
            ),
            meteringPointIds: (
                this.invertersForm.value.meteringPointIds ?? []
            ).map((mp: any) => ({
                meteringPointId: String(mp.meteringPointId ?? '').trim(),
                meterType: String(mp.meterType ?? '').trim(),
                isMain: !!mp.isMain
            })),
            model: String(this.invertersForm.value.model ?? '').trim(),
            plantId: Number(
                this.currentPlantId ?? this.invertersForm.value.plantId ?? 0
            ),
            serialNumber: String(
                this.invertersForm.value.serialNumber ?? ''
            ).trim(),
            totalBatteryCapacityKwh: String(
                this.invertersForm.value.totalBatteryCapacityKwhsolarPanels ??
                ''
            ).trim(),
            totalPanelPowerKwh: String(
                this.invertersForm.value.totalPanelPowerKwh ?? ''
            ).trim()
        };
    }

    submitInverterStep() {
        if (!this.currentPlantId || this.currentPlantId <= 0) {
            this.notify(
                this.t('plant_id_not_set_submit_inverter_step'),
                'error'
            );
            return;
        }

        if (!this.validateSolarThresholdBeforeSubmit()) {
            return;
        }

        if (this.invertersForm.invalid) {
            this.notify(this.t('please_fix_highlighted_fields'), 'error');
            this.invertersForm.markAllAsTouched();
            return;
        }

        const req = this.buildAddInverterRequest();
        const mains = (req.meteringPointIds || []).filter(
            (mp: { isMain: any }) => !!mp.isMain
        );

        if (mains.length !== 1) {
            this.invertersForm.setErrors({ mainCountInvalid: true });
            this.notify(this.t('exactly_one_metering_point_main'), 'error');
            return;
        }

        this.isSubmittingInverter = true;
        this.plantsSvc.addPlantInverter(req).subscribe({
            next: () => {
                this.isSubmittingInverter = false;
                this.notify(this.t('inverter_saved_successfully'), 'success');

                setTimeout(() => {
                    this.router.navigate(['users/users-list']);
                }, 3000);
            },
            error: err => {
                this.isSubmittingInverter = false;
                this.handleError(err, 'add-inverter');
            }
        });
    }

    private buildPlantDtoFromForms(): PlantDto {
        const p = this.personalForm.getRawValue() as any;
        const loc = (this.personalForm.get('location')?.value as unknown) as {
            lat?: number;
            lng?: number;
        } | null;

        return {
            plantOwner: p.plantOwner,
            email: p.email || undefined,
            phoneNumber: p.phone || undefined,
            address: p.address || undefined,
            userId: p.userId || undefined,
            cityId: p.cityId || undefined,
            latitude: loc?.lat ?? undefined,
            longitude: loc?.lng ?? undefined,
            username: p.username,
            priceClass: p.priceClass || undefined,
            customerType: p.customerType || undefined,
            installationDate: installationDateToString(p.installationDate),
            lowPriceThreshold:
                p.lowPriceThreshold != null
                    ? String(p.lowPriceThreshold)
                    : undefined,
            plantImageUrl: this.fileHandle || undefined
        };
    }

    private extractPlantId(res: any): number | null {
        return res?.id ?? res?.plantId ?? res?.plant?.id ?? null;
    }

    onStepChange(ev: any) {
        if (ev.selectedIndex === 1 && !this.currentPlantId) {
            Promise.resolve().then(() => (this.stepper.selectedIndex = 0));
            this.notify(this.t('please_create_plant_first'), 'warn');
        }
    }

    private async uploadSelectedImage(): Promise<void> {
        if (!this.selectedFile || !this.fileHandle) {
            throw new Error(this.t('no_file_or_filehandle'));
        }

        const timeoutMs = 30_000;
        const intervalMs = 100;

        return new Promise<void>((resolve, reject) => {
            const startTime = Date.now();

            const check = setInterval(() => {
                if (this.uploadedImageUrl) {
                    clearInterval(check);
                    clearTimeout(timeout);
                    this.personalForm.patchValue({
                        plantImageUrl: this.uploadedImageUrl
                    });
                    resolve();
                    return;
                }

                if (Date.now() - startTime > timeoutMs) {
                    clearInterval(check);
                    clearTimeout(timeout);
                    const error = new Error(this.t('image_upload_timed_out'));
                    this.uploadError = error.message;
                    reject(error);
                }
            }, intervalMs);

            const timeout = setTimeout(() => {
                clearInterval(check);
                const error = new Error(this.t('image_upload_timed_out'));
                this.uploadError = error.message;
                reject(error);
            }, timeoutMs);
        });
    }

    onClickFirstStep() {
        if (this.personalForm.invalid) {
            this.personalForm.markAllAsTouched();
            this.notify(this.t('please_complete_required_fields'), 'warn');
            return;
        }

        if (this.mode === 'add') {
            this.createPlant();
        } else if (this.mode === 'update') {
            if (!this.currentPlantId) {
                this.notify(this.t('plant_id_not_set_update_plant'), 'error');
                return;
            }
            this.updatePlant();
        }
    }

    createPlant() {
        if (this.personalForm.invalid) {
            this.personalForm.markAllAsTouched();
            return;
        }

        const dto = this.buildPlantDtoFromForms();
        this.isSubmitting = true;

        this.plantsSvc.addNewPlant(dto).subscribe({
            next: res => {
                this.isSubmitting = false;
                const id = this.extractPlantId(res);

                if (!id) {
                    this.notify(this.t('no_plant_id_in_response'), 'error');
                    return;
                }

                this.currentPlantId = id;

                const pidCtrl = this.invertersForm.get('plantId')!;
                pidCtrl.setValue(id);
                pidCtrl.markAsDirty();
                pidCtrl.updateValueAndValidity({ onlySelf: true });
                this.invertersForm.updateValueAndValidity();

                this.firstStepCompleted = true;

                if (this.mode === 'update') {
                    this.personalForm
                        .get('location')
                        ?.enable({ emitEvent: false });
                    this.personalForm
                        .get('latitude')
                        ?.enable({ emitEvent: false });
                    this.personalForm
                        .get('longitude')
                        ?.enable({ emitEvent: false });
                } else {
                    if (this.firstStepCompleted) {
                        this.personalForm
                            .get('location')
                            ?.disable({ emitEvent: false });
                        this.personalForm
                            .get('latitude')
                            ?.disable({ emitEvent: false });
                        this.personalForm
                            .get('longitude')
                            ?.disable({ emitEvent: false });
                    } else {
                        this.personalForm
                            .get('location')
                            ?.enable({ emitEvent: false });
                        this.personalForm
                            .get('latitude')
                            ?.enable({ emitEvent: false });
                        this.personalForm
                            .get('longitude')
                            ?.enable({ emitEvent: false });
                    }
                }

                this.notify(
                    this.t('personal_information_saved_successfully'),
                    'success'
                );

                if (this.selectedFile) {
                    this.isUploadingImage = true;
                    this.uploadProgress = 0;
                    this.uploadError = null;

                    this.uploadSelectedImage()
                        .then(() => {
                            this.stepper.next();
                        })
                        .catch(e => {
                            this.uploadError =
                                e?.message || this.t('image_upload_failed');
                            this.notify(this.uploadError, 'error');
                        })
                        .finally(() => {
                            this.isUploadingImage = false;
                        });
                } else {
                    this.stepper.next();
                }
            },
            error: err => {
                this.isSubmitting = false;
                this.handleError(err, 'add-plant');
            }
        });
    }

    private applyInitialData(data: any): void {
        if (!data) {
            console.warn('[applyInitialData] No data found:', data);
            return;
        }

        const firstDefined = (...vals: any[]) => {
            for (const v of vals) {
                if (v !== undefined && v !== null) return v;
            }
            return undefined;
        };

        const p = data.personal ?? data.plantInfo ?? data._plantInfo ?? null;

        if (!p) {
            console.warn('[applyInitialData] No personal data found:', data);
            return;
        }

        if (!this.personalForm || !this.invertersForm) {
            setTimeout(() => this.applyInitialData(data), 50);
            return;
        }

        const ensureEnabled = (name: string) => {
            const ctrl = this.personalForm.get(name);
            if (ctrl && ctrl.disabled) {
                try {
                    ctrl.enable({ emitEvent: false });
                } catch { }
            }
        };

        [
            'plantOwner',
            'email',
            'username',
            'phone',
            'address',
            'cityId',
            'userId'
        ].forEach(ensureEnabled);

        const plantOwner = firstDefined(
            p.plantOwner,
            p._plantOwner,
            p._PlantOwner,
            ''
        );
        const email = firstDefined(p.email, p._email, p._Email, '');
        const username = firstDefined(p.username, p._userName, p.userName, '');
        const phone = firstDefined(p.phone, p._phoneNumber, p.phoneNumber, '');
        const address = firstDefined(
            p.address,
            p._address,
            p.Address,
            p._Address,
            ''
        );
        const cityId = firstDefined(p.cityId, p._cityId, null);
        const cityTitle = firstDefined(
            p.cityTitle,
            p._cityTitle,
            p.cityName,
            p._cityName,
            undefined
        );
        const timeZoneId = firstDefined(p.timeZoneId, p._timeZoneId, undefined);
        const priceClass = firstDefined(p.priceClass, p._priceClass, undefined);
        const lowPriceThreshold = firstDefined(
            p.lowPriceThreshold,
            p._lowPriceThreshold,
            undefined
        );
        const customerType = firstDefined(
            p.customerType,
            p._customerType,
            undefined
        );
        const userId = firstDefined(p.userId, p._userId, p._userID, undefined);
        const latitude = firstDefined(p.latitude, p._latitude, undefined);
        const longitude = firstDefined(p.longitude, p._longitude, undefined);
        const installationDate = firstDefined(
            p.installationDate,
            p._installationDate,
            null
        );
        const personalPatch: any = {};

        if (plantOwner !== undefined)
            personalPatch.plantOwner = String(plantOwner);
        if (email !== undefined) personalPatch.email = String(email);
        if (username !== undefined) personalPatch.username = String(username);
        if (phone !== undefined) personalPatch.phone = String(phone);
        if (address !== undefined) personalPatch.address = String(address);
        if (cityId !== undefined && cityId !== null)
            personalPatch.cityId = Number(cityId);
        if (userId !== undefined) personalPatch.userId = Number(userId);
        if (priceClass !== undefined) personalPatch.priceClass = priceClass;
        if (lowPriceThreshold !== undefined)
            personalPatch.lowPriceThreshold = lowPriceThreshold;
        if (customerType !== undefined)
            personalPatch.customerType = customerType;

        if (latitude != null && longitude != null) {
            personalPatch.location = {
                lat: Number(latitude),
                lng: Number(longitude)
            };
            personalPatch.latitude = Number(latitude);
            personalPatch.longitude = Number(longitude);
        }
        personalPatch.installationDate =
            installationDateFromString(installationDate);
        this.personalForm.patchValue(personalPatch, { emitEvent: false });
        this.personalForm.updateValueAndValidity({ emitEvent: false });
        const initialCt = (personalPatch.customerType ?? '') as string;
        if (initialCt) {
            this.pendingCustomerType = initialCt;
            this.applyPendingCustomerType();
        }

        if (cityTitle) {
            this.selectedCity = {
                id: Number(cityId ?? 0),
                title: String(cityTitle),
                timeZoneId: String(timeZoneId ?? '')
            };

            try {
                this.citySearchCtrl.setValue(this.selectedCity.title, {
                    emitEvent: false
                });
            } catch { }
        } else if (cityId != null) {
            try {
                this.personalForm.patchValue(
                    { cityId: Number(cityId) },
                    { emitEvent: false }
                );
            } catch { }
        }

        if (personalPatch.cityId && cityTitle) {
            this.selectedCity = {
                id: personalPatch.cityId,
                title: String(cityTitle),
                timeZoneId: ''
            };

            this.citySearchCtrl.setValue(String(cityTitle), {
                emitEvent: false
            });

            this.cityService
                .getTimeZoneIdByCityId(personalPatch.cityId)
                .pipe(take(1))
                .subscribe(tz => {
                    const resolvedTz = tz ?? '';
                    this.selectedCity = {
                        ...this.selectedCity!,
                        timeZoneId: resolvedTz
                    };

                    this.personalForm.patchValue(
                        {
                            cityId: personalPatch.cityId,
                            timeZoneId: resolvedTz
                        },
                        { emitEvent: false }
                    );

                    this.personalForm
                        .get('timeZoneId')
                        ?.updateValueAndValidity({ onlySelf: true });

                    try {
                        this.cdr.detectChanges();
                    } catch { }
                });
        }

        const inverter =
            data.inverter ??
            data.selectedInverter ??
            data.inverters?.[0] ??
            data.plantsDetail?.inverters?.[0] ??
            null;

        if (inverter) {
            const brand = firstDefined(inverter.brand, inverter._brand, '');
            const model = firstDefined(inverter.model, inverter._model, '');
            const serialNumber = firstDefined(
                inverter.serialNumber,
                inverter._serialNumber,
                ''
            );
            const maxProductionCapacity = firstDefined(
                inverter.maxProductionCapacity,
                inverter._maxProductionCapacity,
                ''
            );
            const totalBatteryCapacityKwhsolarPanels = firstDefined(
                inverter.totalBatteryCapacityKwh,
                inverter._totalBatteryCapacityKwh,
                ''
            );
            const totalPanelPowerKwh = firstDefined(
                inverter.totalPanelPowerKwh,
                inverter._totalPanelPowerKwh,
                ''
            );
            const batteryChargePowerRateKw = firstDefined(
                inverter.batteryChargePowerRateKw,
                inverter._batteryChargePowerRateKw,
                ''
            );
            const solarFactorThreshold = this.normalizeSolarFactorThreshold(
                firstDefined(
                    inverter.solarFactorThreshold,
                    inverter._solarFactorThreshold,
                    ''
                )
            );

            this.invertersForm.patchValue(
                {
                    brand: String(brand ?? ''),
                    model: String(model ?? ''),
                    serialNumber: String(serialNumber ?? ''),
                    maxProductionCapacity: String(maxProductionCapacity ?? ''),
                    totalBatteryCapacityKwhsolarPanels: String(
                        totalBatteryCapacityKwhsolarPanels ?? ''
                    ),
                    totalPanelPowerKwh: String(totalPanelPowerKwh ?? ''),
                    batteryChargePowerRateKw: String(
                        batteryChargePowerRateKw ?? ''
                    ),
                    solarFactorThreshold,
                    plantId: Number(
                        this.currentPlantId ??
                        this.plantId ??
                        this.invertersForm.get('plantId')?.value ??
                        0
                    )
                },
                { emitEvent: false }
            );

            this.brandSearchCtrl.setValue(String(brand ?? ''), {
                emitEvent: false
            });
            this.modelOptions =
                this.brandToModels.get(String(brand ?? '').trim()) ?? [];
            this.modelSearchCtrl.setValue(String(model ?? ''), {
                emitEvent: false
            });

            const meteringPoints =
                inverter.meteringPoints ??
                inverter._meteringPoints ??
                inverter.meteringPointIds ??
                inverter._meteringPointIds ??
                [];

            this.meteringPointsFA.clear();

            if (Array.isArray(meteringPoints) && meteringPoints.length) {
                meteringPoints.forEach((mp: any) => {
                    this.meteringPointsFA.push(
                        this.createMeteringPointRow({
                            meteringPointId: String(
                                firstDefined(
                                    mp.meteringPointId,
                                    mp._meteringPointId,
                                    ''
                                )
                            ),
                            meterType: String(
                                firstDefined(mp.meterType, mp._meterType, '')
                            ),
                            isMain: !!firstDefined(mp.isMain, mp._isMain, false)
                        })
                    );
                });
            } else if (this.meteringPointsFA.length === 0) {
                this.addMeteringPoint();
            }

            this.syncMeteringPointsControl();
            this.invertersForm.updateValueAndValidity({ emitEvent: false });
        }

        this.initialData = {
            ...(this.initialData || {}),
            plantOwner:
                personalPatch.plantOwner ?? this.initialData?.plantOwner,
            email: personalPatch.email ?? this.initialData?.email,
            username: personalPatch.username ?? this.initialData?.username,
            phoneNumber: personalPatch.phone ?? this.initialData?.phoneNumber,
            cityId: personalPatch.cityId ?? this.initialData?.cityId,
            priceClass:
                personalPatch.priceClass ?? this.initialData?.priceClass,
            customerType:
                personalPatch.customerType ?? this.initialData?.customerType,
            userId: personalPatch.userId ?? this.initialData?.userId
        };

        try {
            this.cdr.detectChanges();
        } catch { }
    }

    updatePlant() {
        if (this.personalForm.invalid) {
            this.personalForm.markAllAsTouched();
            this.notify(this.t('please_complete_required_fields'), 'error');
            return;
        }

        if (!this.currentPlantId || this.currentPlantId <= 0) {
            this.notify(this.t('plant_id_not_set_update_plant'), 'error');
            return;
        }

        const dto: PlantDto = this.buildPlantDtoFromForms();
        const cityId = this.personalForm.get('cityId')?.value ?? 0;
        const lowPriceThresholdRaw = this.personalForm.get('lowPriceThreshold')
            ?.value;
        const lowPriceThreshold =
            lowPriceThresholdRaw && lowPriceThresholdRaw !== ''
                ? lowPriceThresholdRaw.toString()
                : '0';
        const priceClass = this.personalForm.get('priceClass')?.value ?? '';
        const customerType = this.personalForm.get('customerType')?.value ?? '';

        this.isSubmitting = true;

        this.plantsSvc
            .updatePlant(this.currentPlantId, dto, {
                cityId,
                lowPriceThreshold,
                priceClass,
                customerType
            })
            .subscribe({
                next: () => {
                    this.isSubmitting = false;
                    this.notify(
                        this.t('plant_updated_successfully'),
                        'success'
                    );
                    this.router.navigate([
                        '/plants/plants-detail',
                        this.currentPlantId
                    ]);
                },
                error: err => {
                    this.isSubmitting = false;
                    this.handleError(err, 'update-plant');
                }
            });
    }

    openAddCity(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
        this.cityAutoTrigger?.closePanel();
        this.cityInput?.nativeElement.blur();

        const ref = this.dialog.open(AddNewCityModalComponent, {
            width: '720px',
            maxWidth: '92vw',
            height: 'auto',
            maxHeight: '90vh',
            autoFocus: false,
            restoreFocus: false,
            panelClass: ['city-dialog', 'elevated-dialog'],
            backdropClass: 'dim-backdrop'
        });

        ref.afterClosed().subscribe(
            (city?: { id: number; title: string; timeZoneId: string }) => {
                if (!city) return;
                this.selectedCity = city;
                this.citySearchCtrl.setValue(city.title, { emitEvent: false });
                this.personalForm.patchValue(
                    { cityId: city.id, timeZoneId: city.timeZoneId },
                    { emitEvent: false }
                );
                this.personalForm.get('cityId')?.updateValueAndValidity();
            }
        );
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 8000
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                    ? 'snack-error'
                    : type === 'warn'
                        ? 'snack-warn'
                        : 'snack-info';

        this.sb.open(msg, this.t('close'), {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }

    onFileHandleReady(fileHandle: string) {
        this.fileHandle = fileHandle;
    }

    onImageUploaded(event: { url: string; fileHandle: string }) {
        this.uploadedImageUrl = event.url;
        this.fileHandle = event.fileHandle;
        this.personalForm.patchValue({ plantImageUrl: event.fileHandle });
    }

    private applyPendingCustomerType(): void {
        if (!this.pendingCustomerType) return;
        if (!this.optionsLoaded) return;

        const ct = this.pendingCustomerType;
        this.pendingCustomerType = null;

        const ctrl = this.personalForm.get('customerType');
        if (this.customerTypeOptions.includes(ct)) {
            ctrl?.setValue(ct, { emitEvent: false });
        } else {
            ctrl?.setValue(null, { emitEvent: false });
        }

        ctrl?.updateValueAndValidity({ onlySelf: true });

        try {
            this.cdr.detectChanges();
        } catch { }
    }
    private applyUserContext(context?: UserContext): void {
        if (!context) {
            return;
        }

        this.personalForm.patchValue({
            plantOwner: context.fullName ?? '',
            username: context.userName ?? '',
            email: context.email ?? '',
            phone: context.phoneNumber ?? '',
            userId: context.userId
        }, {
            emitEvent: false
        });

        this.fullName = context.fullName ?? null;
        this.userName = context.userName ?? null;
        this.email = context.email ?? null;
        this.phoneNumber = context.phoneNumber ?? null;
        this.userId = context.userId;
    }

    clearInstallationDate(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.personalForm
            .get('installationDate')
            ?.setValue(null);

        this.personalForm
            .get('installationDate')
            ?.markAsDirty();
    }
}
